import ApiService from '@/api';
import {
  DifficultyModel,
  UserModel,
  PaginationResponseModel,
  PriorityModel,
  StatusModel,
  SubtaskModel,
  TagModel,
  TypeModel,
  TaskInSearchModel,
  Events,
  TaskFileModel,
} from '../../../models';
import {
  AttachSubtaskModel,
  CreateFileFolderModel,
  CreateTaskModel,
  CreateTimerRecordModel,
  CurrentTimerInfoModel,
  DeleteInvalidFileRequestModel,
  EditTimerRecordModel,
  FileImportProgressModel,
  GetSubtasksModel,
  GetTimerRecordsModel,
  GetUserRoleByTaskIdsItemModel,
  GetUserRoleByTaskIdsRequsetModel,
  RemoveFileModel,
  ResponseModel,
  SearchTasksModel,
  StartTimerModel,
  StopTimerModel,
  TaskModel,
  TaskParentModel,
  TaskUpdateDateEndModel,
  TaskUpdateDescriptionModel,
  TaskUpdateDifficultyModel,
  TaskUpdateNameModel,
  TaskUpdateObserversModel,
  TaskUpdatePerformersModel,
  TaskUpdatePriorityModel,
  TaskUpdateStatusModel,
  TaskUpdateTagsModel,
  TaskUpdateTypeModel,
  TimerRecordModel,
} from '../models';
import {
  mapAttachSubtaskRequest,
  mapCreateFolderWithoutEntityRequest,
  mapCreateTaskRequest,
  mapCreateTimerRecord,
  mapDeleteInvalidFileRequestModel,
  mapDeleteTimerRecordRequest,
  mapEditTimerRecordRequest,
  mapGetCurrentTimerDateStart,
  mapGetDifficultyListResponse,
  mapGetEmployeesRequest,
  mapGetEmployeesResponse,
  mapGetParentTaskRequest,
  mapGetParentTaskResponse,
  mapGetPrioritiesRequest,
  mapGetPrioritiesResponse,
  mapGetProjectRequest,
  mapGetProjectResponse,
  mapGetStatusesRequest,
  mapGetStatusesResponse,
  mapGetSubtasksRequest,
  mapGetSubtasksResponse,
  mapGetTagsRequest,
  mapGetTagsResponse,
  mapGetTaskDetailRequest,
  mapGetTaskDetailResponse,
  mapGetTimerRecordsRequest,
  mapGetTimerRecordsResponse,
  mapGetTypesRequest,
  mapGetTypesResponse,
  mapGetUserRoleByTaskIdsItemModel,
  mapGetUserRoleByTaskIdsRequsetModel,
  mapRemoveFileRequest,
  mapRemoveSubtaskRequest,
  mapSearchTasksRequest,
  mapSearchTasksResponse,
  mapStartTimerRequest,
  mapStopTimerRequest,
  mapUpdataTaskDifficultyRequest,
  mapUpdateTaskDeadline,
  mapUpdateTaskDescription,
  mapUpdateTaskNameRequest,
  mapUpdateTaskObserversRequest,
  mapUpdateTaskPerformersRequest,
  mapUpdateTaskPriorityRequest,
  mapUpdateTaskStatusRequest,
  mapUpdateTaskTagsRequest,
  mapUpdateTaskTypeRequest,
} from './AdapterServiceMappers';
import { CurrentUser } from 'tir-pm-user-core';
import CurrnetLastTimerRequest from '@/api/Main/dataSource/api/projects/task-timer/i-service/model/CurrnetLastTimerRequest';
import WsResponseModelT from '@/api/File/dataSource/api/schema-builder/server/models/WsResponseModelT';
import OnCompleteAddFileResponse from '@/api/File/dataSource/api/project-manager/files/i-services/models/response/OnCompleteAddFileResponse';
import CreateFolderWithoutEntityRequest from '@/api/File/dataSource/api/project-manager/files/i-services/models/request/CreateFolderWithoutEntityRequest';
import { NamespaceModel } from '@/components/sidebar/models';
import {
  mapGetTreeRequest,
  mapGetTreeResponse,
} from '@/components/sidebar/adapter/AdapterServiceMappers';
import GetGroupedByStatusTaskListResponse from '../models/GetGroupedByStatusTaskListResponse';
import GetGroupedByStatusTaskListRequest from '../models/GetGroupedByStatusTaskListRequest';
import TaskMoveRequestModel from '../models/TaskMoveRequestModel';
import OnFileProgressResponse from '@/api/File/dataSource/api/project-manager/files/i-services/models/response/OnFileProgressResponse';
import WeeekOptionsCheckEnableRequest from '@/api/Weeek/dataSource/api/project-manager/weeek/web/models/weeek-options-ws-controller/request/WeeekOptionsCheckEnableRequest';
import AbortAddFileRequest from '@/api/File/dataSource/api/project-manager/files/i-services/models/request/AbortAddFileRequest';
import AbortVideoRequest from '@/api/File/dataSource/api/project-manager/files/i-services/models/request/AbortVideoRequest';
import { ProjectModel } from '@/models';

export class AdapterService {
  private readonly api = new ApiService();

  /**
   * * Подписаться на события задачи
   */
  async subscribeToTaskChange(taskId: number) {
    await this.api.Main.ProjectTaskSaveService.subscribeToDetailTaskAsync({ TaskId: taskId });
  }
  /**
   * * Подписаться на события задачи
   */
  async unSubscribeToTaskChange(taskId: number) {
    await this.api.Main.ProjectTaskSaveService.unSubscribeToDetailTaskAsync({ TaskId: taskId });
  }

  /**
   * * Событие успешного завершения загрузки
   */
  OnCompleteAddFileEvent = new Events<number>();
  /**Событие прогресса импорта файлов */
  FileImportProgressEvent = new Events<FileImportProgressModel>();

  /**Функция обработчик при добавлении файла */
  private onCompleteAddFile = (res: WsResponseModelT<OnCompleteAddFileResponse>) => {
    if (res.IsSuccess && res.Value?.FileId) this.OnCompleteAddFileEvent.trigger(res.Value.FileId);
  };

  /**Функция обработчик при прогрессе импорта файла */
  private onFileImportProgress = (res: WsResponseModelT<OnFileProgressResponse>) => {
    if (res.IsSuccess && res.Value?.FileId)
      this.FileImportProgressEvent.trigger(
        new FileImportProgressModel({
          FileId: res.Value.FileId,
          Progress: res.Value.ProgressDownload,
          UploadId: res.Value.UploadId,
        }),
      );
  };

  constructor() {
    this.api.File.FileService.OnCompleteAddFileEvent.on(this.onCompleteAddFile);
    this.api.File.FileService.OnFileProgressEvent.on(this.onFileImportProgress);
  }

  /**
   * * Создание папки для загрузки файлов в еще не созданную задачу
   * @param data
   * @returns
   */
  createFolderWithoutEntity(data: CreateFileFolderModel) {
    return new Promise<ResponseModel<string>>(async (resolve) => {
      const result = new ResponseModel<string>();

      await this.api.File.FolderService.createFolderWithoutEntityAsync(
        mapCreateFolderWithoutEntityRequest(data),
      )
        .then((resp) => {
          result.Value = resp.FolderId;
          return resolve(result);
        })
        .catch((e) => {
          console.error('ERROR createFolderWithoutEntity');
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          return resolve(result);
        });
    });
  }
  /**
   * * Получить дерево неймспейсов папок и очередей
   * @param projectId ID проекта
   * @returns
   */
  getTree(projectId: number, onlyNamespaces?: boolean, onlyConfigured?: boolean) {
    return new Promise<ResponseModel<Array<NamespaceModel>>>(async (resolve) => {
      const result = new ResponseModel<Array<NamespaceModel>>();
      const request = mapGetTreeRequest(projectId, onlyConfigured);
      const query = onlyNamespaces
        ? this.api.Main.ProjectNamespaceService.getListAsync(request as any)
        : this.api.Main.ProjectNamespaceService.getTreeAsync(request);

      await query
        .then((resp) => {
          const response = onlyNamespaces ? { Namespaces: resp.Items } : resp;
          result.Value = mapGetTreeResponse(response);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getTree', e);
          return resolve(result);
        });
    });
  }
  /**
   * * Получить список статусов
   * @param queueId ID очереди
   * @returns
   */
  getStatuses(queueId: string) {
    return new Promise<ResponseModel<Array<StatusModel>>>(async (resolve) => {
      const result = new ResponseModel<Array<StatusModel>>();

      await this.api.Main.TaskStatusListService.getListAsync(mapGetStatusesRequest(queueId))
        .then((resp) => {
          result.Value = mapGetStatusesResponse(resp);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getStatuses', e);
          return resolve(result);
        });
    });
  }

  /**получить список задач очереди */
  getTasksQueue(request: GetGroupedByStatusTaskListRequest) {
    return new Promise<ResponseModel<GetGroupedByStatusTaskListResponse>>(async (resolve) => {
      const result = new ResponseModel<GetGroupedByStatusTaskListResponse>();

      await this.api.Main.ProjectTaskGetService.getGroupedByStatusTaskListAsync(request)
        .then((resp) => {
          result.Value = resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getPriorities', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Получить список приоритетов
   * @param queueId ID очереди
   * @returns
   */
  getPriorities(queueId: string) {
    return new Promise<ResponseModel<Array<PriorityModel>>>(async (resolve) => {
      const result = new ResponseModel<Array<PriorityModel>>();

      await this.api.Main.TaskPriorityGetService.getPrioritiesListAsync(
        mapGetPrioritiesRequest(queueId),
      )
        .then((resp) => {
          result.Value = mapGetPrioritiesResponse(resp);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getPriorities', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Получить типы задач
   * @param namespaceId ID неймспейса
   * @returns
   */
  getTypes(namespaceId: string) {
    return new Promise<ResponseModel<Array<TypeModel>>>(async (resolve) => {
      const result = new ResponseModel<Array<TypeModel>>();

      await this.api.Main.TaskTypeListService.getListTaskTypeAsync(mapGetTypesRequest(namespaceId))
        .then((resp) => {
          result.Value = mapGetTypesResponse(resp);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getTypes', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Получить список уровней сложности
   * @returns
   */
  getDifficultyList(nemespaceId?: number & string) {
    return new Promise<ResponseModel<Array<DifficultyModel>>>(async (resolve) => {
      const result = new ResponseModel<Array<DifficultyModel>>();

      await this.api.Main.TaskDifficultyListService.getDifficultyListAsync({
        NamespacesId: [nemespaceId],
      })
        .then((resp) => {
          result.Value = mapGetDifficultyListResponse(resp);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getDifficultyList', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Получить список тегов
   * @param projectId Id проекта
   * @returns
   */
  getTags(projectId: number) {
    return new Promise<ResponseModel<Array<TagModel>>>(async (resolve) => {
      const result = new ResponseModel<Array<TagModel>>();

      await this.api.Main.TagGetService.getTagsByProjectIdAsync(mapGetTagsRequest(projectId))
        .then((resp) => {
          result.Value = mapGetTagsResponse(resp);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getTags', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Получить список сотрудников
   * @param queueId ID очереди
   * @returns
   */
  getEmployees(queueId: string) {
    return new Promise<ResponseModel<Array<UserModel>>>(async (resolve) => {
      const result = new ResponseModel<Array<UserModel>>();

      await this.api.Main.PerformersListService.getUserListAccessQueueTaskAsync(
        mapGetEmployeesRequest(queueId),
      )
        .then(async (resp) => {
          let employees = mapGetEmployeesResponse(resp);
          employees = employees.filter((employee) => employee.Id !== CurrentUser.Id);
          employees.unshift(
            new UserModel({
              Id: CurrentUser.Id,
              FirstName: CurrentUser.FirstName,
              LastName: CurrentUser.LastName,
              MiddleName: CurrentUser.MiddleName,
              Email: CurrentUser.Email,
              ImageUrl: CurrentUser.Foto,
              AvatarColor: CurrentUser.AvatarColor,
            }),
          );
          result.Value = employees;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getEmployees', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Получение детальной информации о задаче
   * @param taskId ID задачи
   * @returns
   */
  getTaskDetail(taskId: number) {
    return new Promise<ResponseModel<TaskModel>>(async (resolve) => {
      const result = new ResponseModel<TaskModel>();

      await this.api.Main.ProjectTaskGetService.getByIdAsync(mapGetTaskDetailRequest(taskId))
        .then((resp) => {
          result.Value = mapGetTaskDetailResponse(resp);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getTaskDetail', e);
          return resolve(result);
        });
    });
  }
  /**Получение доступа по номеру задачи */
  getUserRoleByTaskIdsAsync(
    request: GetUserRoleByTaskIdsRequsetModel,
  ): Promise<ResponseModel<GetUserRoleByTaskIdsItemModel[]>> {
    return new Promise<ResponseModel<GetUserRoleByTaskIdsItemModel[]>>(async (resolve) => {
      const result = new ResponseModel<GetUserRoleByTaskIdsItemModel[]>();

      await this.api.Main.PerformersListService.getUserRoleByTaskIdsAsync(
        mapGetUserRoleByTaskIdsRequsetModel(request),
      )
        .then((resp) => {
          result.Value = mapGetUserRoleByTaskIdsItemModel(resp);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getTaskDetail', e);
          return resolve(result);
        });
    });
  }
  /**
   * * Создание задачи
   * @param request Запрос
   * @returns
   */
  createTask(request: CreateTaskModel) {
    return new Promise<ResponseModel<number>>(async (resolve) => {
      const result = new ResponseModel<number>();

      await this.api.Main.ProjectTaskSaveService.taskCreateAsync(mapCreateTaskRequest(request))
        .then((resp) => {
          result.Value = resp.Id;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR createTask', e);
          return resolve(result);
        });
    });
  }

  /**Перенос задачи в другую очередь */
  taskMove(request: TaskMoveRequestModel) {
    return new Promise<ResponseModel<Boolean>>(async (resolve) => {
      const result = new ResponseModel<Boolean>();

      await this.api.Main.ProjectTaskSaveService.taskMoveAsync(request)
        .then((resp) => {
          result.Value = resp;
          resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR moveTask', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Изменить название задачи
   * @param request
   * @returns
   */
  updateTaskName(request: TaskUpdateNameModel) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.ProjectTaskSaveService.updateNameAsync(mapUpdateTaskNameRequest(request))
        .then((resp) => {
          document.dispatchEvent(
            new CustomEvent('updateTaskName', {
              detail: { taskId: request.Id, taskName: request.Name },
            }),
          );
          result.Value = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR createTask', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Изменить статус задачи
   * @param request
   * @returns
   */
  updateTaskStatus(request: TaskUpdateStatusModel) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.ProjectTaskSaveService.updateStatusAsync(
        mapUpdateTaskStatusRequest(request),
      )
        .then((resp) => {
          result.Value = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR updateTaskStatus', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Изменить приоритет задачи
   * @param request
   * @returns
   */
  updateTaskPriority(request: TaskUpdatePriorityModel) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.ProjectTaskSaveService.updatePriorityAsync(
        mapUpdateTaskPriorityRequest(request),
      )
        .then((resp) => {
          result.Value = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR updateTaskPriority', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Изменить тип задачи
   * @param request
   * @returns
   */
  updateTaskType(request: TaskUpdateTypeModel) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();
      await this.api.Main.ProjectTaskSaveService.updateTaskTypeAsync(
        mapUpdateTaskTypeRequest(request),
      )
        .then((resp) => {
          result.Value = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR updateTaskType', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Изменить уровень сложности задачи
   * @param request
   * @returns
   */
  updateTaskDifficulty(request: TaskUpdateDifficultyModel) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.ProjectTaskSaveService.updateDifficultyAsync(
        mapUpdataTaskDifficultyRequest(request),
      )
        .then((resp) => {
          result.Value = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR updateTaskDifficulty', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Изменить список исполнителей в задаче
   * @param request
   * @returns
   */
  updateTaskPerformers(request: TaskUpdatePerformersModel) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.ProjectTaskSaveService.updatePerformersAsync(
        mapUpdateTaskPerformersRequest(request),
      )
        .then((resp) => {
          result.Value = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR updateTaskPerformers', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Обновить список наблюдателей
   * @param request
   * @returns
   */
  updateTaskObservers(request: TaskUpdateObserversModel) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.ProjectTaskSaveService.updateObserverAsync(
        mapUpdateTaskObserversRequest(request),
      )
        .then((resp) => {
          result.Value = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR updateTaskObservers', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Обновить список тегов
   * @param request
   * @returns
   */
  updateTaskTags(request: TaskUpdateTagsModel) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.ProjectTaskSaveService.updateTagsInTaskAsync(
        mapUpdateTaskTagsRequest(request),
      )
        .then((resp) => {
          result.Value = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR updateTaskTags', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Обновить описание
   * @param request
   * @returns
   */
  updateTaskDescription(request: TaskUpdateDescriptionModel) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.ProjectTaskSaveService.updateDescriptionAsync(
        mapUpdateTaskDescription(request),
      )
        .then((resp) => {
          result.Value = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR updateTaskDescription', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Обновить дату дедлайна
   * @param request
   * @returns
   */
  updateDeadline(request: TaskUpdateDateEndModel) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.ProjectTaskSaveService.updateDateEndAsync(mapUpdateTaskDeadline(request))
        .then((resp) => {
          result.Value = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR updateDeadline', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Получить подзадачи определенной задачи
   * @param request
   * @returns
   */
  getSubtasks(request: GetSubtasksModel) {
    return new Promise<PaginationResponseModel<Array<SubtaskModel>>>(async (resolve) => {
      let result = new PaginationResponseModel<Array<SubtaskModel>>();

      await this.api.Main.ProjectTaskGetService.getByParentTaskListAsync(
        mapGetSubtasksRequest(request),
      )
        .then((resp) => {
          result = mapGetSubtasksResponse(resp);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getSubtasks', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Поиск задач
   * @param request Запрос
   * @returns
   */
  searchTasks(request: SearchTasksModel) {
    return new Promise<PaginationResponseModel<Array<TaskInSearchModel>>>(async (resolve) => {
      let result = new PaginationResponseModel<Array<TaskInSearchModel>>();

      await this.api.Main.ProjectTaskGetService.getTaskShortListAsync(
        mapSearchTasksRequest(request),
      )
        .then((resp) => {
          result = mapSearchTasksResponse(resp);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR searchTasks', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Прикрепить подзадачу
   * @param request
   * @returns
   */
  attachSubtask(request: AttachSubtaskModel) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.ProjectTaskSaveService.attachSubtaskAsync(
        mapAttachSubtaskRequest(request),
      )
        .then((resp) => {
          result.Value = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR attachSubtask', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Убрать подзадачу из задачи
   * @param subtaskId ID подазадчи
   * @returns
   */
  removeSubtask(subtaskId: number) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.ProjectTaskSaveService.removeSubtaskAsync(
        mapRemoveSubtaskRequest(subtaskId),
      )
        .then((resp) => {
          result.Value = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR removeSubtask', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Получить список таймеров
   * @param request
   * @returns
   */
  getTimerRecords(request: GetTimerRecordsModel): Promise<ResponseModel<Array<TimerRecordModel>>> {
    return new Promise<ResponseModel<Array<TimerRecordModel>>>(async (resolve) => {
      const result = new ResponseModel<Array<TimerRecordModel>>();

      await this.api.Main.TaskTimersService.getListAsync(mapGetTimerRecordsRequest(request))
        .then((resp) => {
          result.Value = mapGetTimerRecordsResponse(resp, request.IsOnlyEnded);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getTimerRecords', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Запустить таймер
   * @param request
   * @returns
   */
  startTimer(request: StartTimerModel) {
    return new Promise<ResponseModel<number>>(async (resolve) => {
      const result = new ResponseModel<number>();

      await this.api.Main.TaskTimersService.timerStartAsync(mapStartTimerRequest(request))
        .then((resp) => {
          result.Value = resp.Id;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR startTimer', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Остновить таймер
   * @param request
   * @returns
   */
  stopTimer(request: StopTimerModel) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.TaskTimersService.stopTimerAsync(mapStopTimerRequest(request))
        .then((resp) => {
          result.Value = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR stopTimer', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Создать запись таймера
   * @param request
   * @returns
   */
  createTimerRecord(request: CreateTimerRecordModel) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.TaskTimersService.timerCreateAsync(mapCreateTimerRecord(request))
        .then((resp) => {
          result.Value = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR createTimerRecord', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Получить дату старта последнего таймера
   * @param taskId ID задачи
   * @returns
   */
  getCurrentTimerDateStart(taskId: number) {
    return new Promise<ResponseModel<CurrentTimerInfoModel | undefined>>(async (resolve) => {
      const result = new ResponseModel<CurrentTimerInfoModel | undefined>();

      await this.api.Main.TaskTimersService.currnetLastTimerAsync(
        mapGetCurrentTimerDateStart(taskId),
      )
        .then((resp) => {
          if (resp?.Id && resp.DateStart)
            result.Value = new CurrentTimerInfoModel({
              TimerId: resp.Id,
              DateStart: new Date(resp.DateStart + 'Z'),
            });
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getCurrentTimerDateStart', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Проверить запущен ли другой таймер
   * @param taskId ID задачи
   * @returns Id запущенного таймера
   */
  checkStartedOtherTimer(taskId: number) {
    return new Promise<ResponseModel<number>>(async (resolve) => {
      const result = new ResponseModel<number>();

      await this.api.Main.TaskTimersService.currnetLastTimerAsync(
        new CurrnetLastTimerRequest({ WithTaskInfo: true }),
      )
        .then((resp) => {
          result.Value = !!resp?.Id && resp.TaskId !== taskId ? resp.Id : undefined;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR checkStartedOtherTimer', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Удалить запись таймера
   * @param timerId ID таймера
   * @returns
   */
  deleteTimerRecord(timerId: number) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.TaskTimersService.deleteAsync(mapDeleteTimerRecordRequest(timerId))
        .then((resp) => {
          result.Value = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR deleteTimerRecord', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Редактирование записи таймера
   * @param data
   * @returns
   */
  editTimerRecord(data: EditTimerRecordModel) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.TaskTimersService.editAsync(mapEditTimerRecordRequest(data))
        .then((resp) => {
          result.Value = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR editTimerRecord', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Удалить файл
   * @param request
   * @returns
   */
  removeFile(request: RemoveFileModel) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.ProjectTaskSaveService.removeFilesAsync(mapRemoveFileRequest(request))
        .then((resp) => {
          result.Value = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR removeFile', e);
          return resolve(result);
        });
    });
  }
  /**Удалить файл до создания задачи */
  removeFileBeforeTaskCreation(
    request: DeleteInvalidFileRequestModel,
  ): Promise<ResponseModel<Boolean>> {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.File.FileService.deleteInvalidFileAsync(
        mapDeleteInvalidFileRequestModel(request),
      )
        .then((resp) => {
          result.Value = !!resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR removeFile', e);
          return resolve(result);
        });
    });
  }

  /**
   * * Получить информацию о родительской задаче
   * @param parentTaskId ID родительской задачи
   * @returns
   */
  getParentTask(parentTaskId: number) {
    return new Promise<ResponseModel<TaskParentModel>>(async (resolve) => {
      const result = new ResponseModel<TaskParentModel>();

      await this.api.Main.ProjectTaskGetService.getShortTaskByIdAsync(
        mapGetParentTaskRequest(parentTaskId),
      )
        .then((resp) => {
          result.Value = mapGetParentTaskResponse(resp);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getParentTask', e);
          return resolve(result);
        });
    });
  }

  /**Проверка включения интеграции проекта weeek */
  checkEnableWeeekImport(projectId: number) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Weeek.WeeekOptionsWsController.checkEnableAsync(
        new WeeekOptionsCheckEnableRequest({
          ProjectId: projectId,
        }),
      )
        .then((resp) => {
          result.Value = resp.WeeekEnable;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR checkEnableWeeekImport', e);
          return resolve(result);
        });
    });
  }

  /**
   * Отмена загрузки файла
   * @param data
   */
  async abortUploadFile(data: TaskFileModel): Promise<ResponseModel> {
    return new Promise<ResponseModel>(async (resolve) => {
      const result = new ResponseModel();

      const request = new AbortAddFileRequest({
        UploadId: data.UploadId,
        FileId: data.Id,
      });

      this.api.File.FileService.abortAddFileAsync(request)
        .then(() => {
          resolve(result);
        })
        .catch((e) => {
          console.error('ERROR abortUploadFile', e);
          result.IsSuccess = false;
          result.ErrorCode = e.ErrorCode;
          result.ErrorMessage = e.ErrorMessage;
          resolve(result);
        });
    });
  }

  /**
   * Отмена загрузки видео
   * @param videoId
   */
  async abortUploadVideo(videoId: string): Promise<ResponseModel> {
    return new Promise<ResponseModel>(async (resolve) => {
      const result = new ResponseModel();

      const request = new AbortVideoRequest({
        VideoId: videoId,
      });

      this.api.File.VideoService.abortVideoAsync(request)
        .then(() => {
          resolve(result);
        })
        .catch((e) => {
          console.error('ERROR abortUploadVideo', e);
          result.IsSuccess = false;
          result.ErrorCode = e.ErrorCode;
          result.ErrorMessage = e.ErrorMessage;
          resolve(result);
        });
    });
  }
}
